import { ClientAvatar } from 'components/card/ClientAvatar'
import { Button } from 'components/button/Button'
import { Modal } from 'components'
import {
    canOpenPhoneLookupMatch,
    formatPhone,
    isUnknownVillageName,
    PhoneLookupMatch
} from 'helpers'
import { getGenderAgeProfile } from 'pages/village/helpers/villageClientHelpers'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonStylesEnum } from 'types/buttonStylesEnum'
import './phoneMatch.scss'

const formatCreatedDate = (value?: string) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString()
}

const PhoneMatchCard = ({
    client,
    canOpen,
    onClick
}: {
    client: PhoneLookupMatch
    canOpen: boolean
    onClick: () => void
}) => {
    const { t } = useTranslation()
    const genderIconUrl = useMemo(
        () => getGenderAgeProfile(t, client.sex, Number(client.age)),
        [client.sex, client.age, t]
    )
    const village = isUnknownVillageName(client.village)
        ? t('enrollment.unknownVillage')
        : client.village

    const body = (
        <div className="card-village-client-v2__content">
            <div className="card-village-client-v2__avatar-container">
                <ClientAvatar
                    genderIconUrl={genderIconUrl || ''}
                    duplicate={Boolean(client.duplicate)}
                    fullname={client.fullname}
                />
            </div>
            <div className="card-village-client-v2__info">
                <div className="card-village-client-v2__header">
                    <div className="card-village-client-v2__id-section">
                        <span className="card-village-client-v2__id">
                            {t('village.id')}: {client.client_code}
                        </span>
                    </div>
                    <h3 className="card-village-client-v2__name">
                        {client.fullname}
                    </h3>
                    {client.phone ? (
                        <p className="card-village-client-v2__phone">
                            {formatPhone(client.phone)}
                        </p>
                    ) : null}
                </div>
                <p className="card-village-client-v2__phone">{village}</p>
                <p className="card-village-client-v2__phone">
                    {t('enrollment.createdOn', {
                        date: formatCreatedDate(client.date_created)
                    })}
                </p>
                {client.is_default || !canOpen ? (
                    <div className="phone-match-chips">
                        {client.is_default ? (
                            <span className="phone-match-default-chip">
                                {t('enrollment.defaultChip')}
                            </span>
                        ) : null}
                        {!canOpen ? (
                            <span className="phone-match-elsewhere-chip">
                                {t('enrollment.assignedElsewhereChip')}
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    )

    if (!canOpen) {
        return (
            <div
                className="card-village-client-v2"
                aria-label="phone-match-assigned-elsewhere"
            >
                {body}
            </div>
        )
    }

    return (
        <button
            type="button"
            className="card-village-client-v2"
            onClick={onClick}
            aria-label="village-client-card"
        >
            {body}
        </button>
    )
}

export const PhoneMatchModal = ({
    show,
    clients,
    onOpen,
    onContinue,
    modification = false,
    assignedVillageIds = []
}: {
    show: boolean
    clients: PhoneLookupMatch[]
    onOpen: (client: PhoneLookupMatch) => void | Promise<void>
    onContinue: () => void
    modification?: boolean
    assignedVillageIds?: string[]
}) => {
    const { t } = useTranslation()

    return (
        <Modal showModal={show} position={'center-expandable'} hideButtons>
            <div className="duplicate-phone-header-container">
                <h2 className="modal--header">
                    {t('enrollment.existingPhoneTitle')}
                </h2>
            </div>
            <div className="phone-match-client-list">
                {clients.map((client) => {
                    const canOpen = canOpenPhoneLookupMatch(
                        client,
                        assignedVillageIds
                    )
                    return (
                        <div
                            key={client.client_code}
                            className="phone-match-client-row"
                        >
                            <PhoneMatchCard
                                client={client}
                                canOpen={canOpen}
                                onClick={() => onOpen(client)}
                            />
                        </div>
                    )
                })}
            </div>
            <div
                className={`phone-match-continue${
                    modification ? ' phone-match-continue--modification' : ''
                }`}
            >
                <Button style={ButtonStylesEnum.SECONDARY} onClick={onContinue}>
                    {t('enrollment.continueAnyway')}
                </Button>
            </div>
        </Modal>
    )
}
